import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../../../../components/Modal';
import Dropdown from '../../../../components/Dropdown';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import { AuthContext } from '../../../../contexts/AuthContext';
import { Modules, RoleCodes, RouteParamsTypes } from '../../../../enums';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES } from '../../../../utils/appConstant';
import { STATUS_CODE } from '../../../../utils/apiConstant';
import { Validation } from '../../../../interfaces/common';
import { ExistingStatementConflict, GenerateStatement, GenerateStatementApi } from '../../../../interfaces/managerStatement.interface';
import { validateGenerateStatementForm } from '../validations';
import { getPreviousMonthAndYear } from '../../../../utils/methods';
import useFetch from '../../../../hooks/useFetch';
import UserService from '../../../../services/user.service';
import CommonService from '../../../../services/common.service';
import ManagerStatementService from '../../../../services/managerStatement.service';

const initialFormData: GenerateStatement = { manager: '', month: '', year: '' };
const initialValidation: Validation = { validate: true, fields: [] };
const initialDropdownSearchData = { manager: '', month: '' };

interface GenerateStatementModalProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (newStatementId: number) => void;
}

const GenerateStatementModal: React.FC<GenerateStatementModalProps> = ({ open, onClose, onGenerated }) => {
  const { t } = useTranslation();
  const { loggedInUser } = useContext(AuthContext);

  const isManagerLocked = loggedInUser?.role_code === RoleCodes.manager;

  const [formData, setFormData] = useState<GenerateStatement>(initialFormData);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [dropdownSearchData, setDropdownSearchData] = useState(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [existingStatement, setExistingStatement] = useState<ExistingStatementConflict | null>(null);

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managerRoleLookup',
    payload: { searchText: '', is_active: '1' },
    skip: isManagerLocked,
    dependency: [isManagerLocked],
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managerRoleLookup',
    payload: { searchText: dropdownSearchData.manager, is_active: '1' },
    skip: isManagerLocked,
    dependency: [dropdownSearchData.manager, isManagerLocked],
  });

  const { data: allMonths } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: '' },
    dependency: [],
  });

  const { data: months } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: dropdownSearchData.month },
    dependency: [dropdownSearchData.month],
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, index) => {
    const year = (currentYear - 2 + index).toString();
    return { label: year, value: year };
  });

  useEffect(() => {
    if (!open) return;

    setFormData((prev) => (
      prev.month === '' && prev.year === ''
        ? { ...prev, ...getPreviousMonthAndYear() }
        : prev
    ));
  }, [open]);

  const handleDropdownSearchChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleInputBlur = useCallback((event: any, name: string) => {
    setValidation(validateGenerateStatementForm({ ...formData, [name]: event.target.value }, !isManagerLocked, name));
  }, [formData, isManagerLocked]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFormData((prev) => ({ ...prev, [name]: event.target.value }));
    setExistingStatement(null);
  }, []);

  const resetState = () => {
    setFormData(initialFormData);
    setValidation(initialValidation);
    setDropdownSearchData(initialDropdownSearchData);
    setExistingStatement(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleViewExistingStatement = () => {
    if (!existingStatement) return;
    window.open(`${Modules.reconciliation}${RouteParamsTypes.managerStatement}/${existingStatement.statement_id}`, '_blank');
  };

  const handleSubmit = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateGenerateStatementForm(formData, !isManagerLocked);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    setLoading(true);

    try {
      const payload: GenerateStatementApi = {
        month: formData.month,
        year: formData.year,
        ...(!isManagerLocked ? { manager_id: formData.manager } : {}),
        create_new_statement: !!existingStatement,
      };

      const response: any = await ManagerStatementService.generate(payload);

      setLoading(false);
      toast.success(<Toast message={APP_SUCCESS_MESSAGES.managerStatementGenerated} />, { icon: false });
      resetState();
      onGenerated(response?.id);
    } catch (e: any) {
      setLoading(false);
      if (e?.data?.code === STATUS_CODE.conflict) {
        setExistingStatement(e?.data?.data ?? null);
      }
    }
  }, [formData, isManagerLocked, existingStatement, onGenerated]);

  const managerValues = isManagerLocked
    ? (loggedInUser?.id ? [String(loggedInUser.id)] : [])
    : (formData.manager ? [formData.manager] : []);

  const managerOptions = isManagerLocked
    ? (loggedInUser?.id ? [{ label: loggedInUser?.full_name ?? '', value: String(loggedInUser.id) }] : [])
    : (managers || []).map((manager: any) => ({ label: manager.name, value: String(manager.id) }));

  const managerAllOptions = isManagerLocked
    ? managerOptions
    : (allManagers || []).map((manager: any) => ({ label: manager.name, value: String(manager.id) }));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('reconciliation.managerStatement.list.pageTexts.modal.title')}
      titleClassName="text-start"
      actionClassName="justify-content-start"
      content={
        <form className="card-body">
          <div className="row g-6 mb-4">
            <div className="col-md-12">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  values={managerValues}
                  required
                  disabled={isManagerLocked}
                  options={managerOptions}
                  allOptions={managerAllOptions}
                  id={VALIDATION_FIELD_MAPPING.manager}
                  name={VALIDATION_FIELD_MAPPING.manager}
                  searchText={dropdownSearchData.manager}
                  onSearch={handleDropdownSearchChange}
                  placeholder={t('reconciliation.managerStatement.list.pageTexts.modal.fields.manager.placeholder')}
                  label={t('reconciliation.managerStatement.list.pageTexts.modal.fields.manager.label')}
                  error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.manager)}
                  errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.manager)?.message ?? ''}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>
          </div>

          <div className="row g-6">
            <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  values={formData.month ? [formData.month] : []}
                  required
                  options={(months || []).map((month: any) => ({ label: month.name, value: month.id }))}
                  allOptions={(allMonths || []).map((month: any) => ({ label: month.name, value: month.id }))}
                  id={VALIDATION_FIELD_MAPPING.month}
                  name={VALIDATION_FIELD_MAPPING.month}
                  searchText={dropdownSearchData.month}
                  onSearch={handleDropdownSearchChange}
                  placeholder={t('reconciliation.managerStatement.list.pageTexts.modal.fields.month.placeholder')}
                  label={t('reconciliation.managerStatement.list.pageTexts.modal.fields.month.label')}
                  error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.month)}
                  errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.month)?.message ?? ''}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  values={formData.year ? [formData.year] : []}
                  required
                  noSearch
                  options={years}
                  allOptions={years}
                  id={VALIDATION_FIELD_MAPPING.year}
                  name={VALIDATION_FIELD_MAPPING.year}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder={t('reconciliation.managerStatement.list.pageTexts.modal.fields.year.placeholder')}
                  label={t('reconciliation.managerStatement.list.pageTexts.modal.fields.year.label')}
                  error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.year)}
                  errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.year)?.message ?? ''}
                />
              </div>
            </div>
          </div>

          {
            existingStatement && (
              <div className="row mt-4">
                <div className="d-flex align-items-center p-3 rounded border border-warning" data-testid="generate-statement-duplicate-warning">
                  <i className="ri-alert-line fs-3 me-3 text-warning"></i>
                  <span className="text-body">
                    {t('reconciliation.managerStatement.list.pageTexts.modal.duplicateWarning1')}
                    <span
                      className="span mb-1 text-decoration-underline hyperlink"
                      onClick={handleViewExistingStatement}
                    >
                      {existingStatement.statement_number}
                    </span>
                    {t('reconciliation.managerStatement.list.pageTexts.modal.duplicateWarning2')}
                  </span>
                </div>
              </div>
            )
          }
        </form>
      }
      action={
        <div className="d-flex gap-3">
          <Button
            className="btn btn-primary"
            text={existingStatement
              ? t('reconciliation.managerStatement.list.pageTexts.modal.createNewBtn')
              : t('reconciliation.managerStatement.list.pageTexts.modal.saveBtn')}
            loading={loading}
            onClick={handleSubmit}
          />
          <Button
            className="btn btn-outline-secondary"
            text={t('reconciliation.managerStatement.list.pageTexts.modal.cancelBtn')}
            onClick={handleClose}
          />
        </div>
      }
      maxWidth="sm"
    />
  );
};

export default GenerateStatementModal;
